import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLinkIcon,
  Card,
  ContentBox,
  ContentPage,
  NoResults,
  Pagination,
  Skeleton,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { DomainStatus, escapeHtml, formatBRL, formatDate, usePageHeader } from "@components/ow";
import { CONTRACT_STATUS, isFixedWithAdjustment, remunerationSummary } from "../../Common/ContractDetail";

// Página da API por requisição (o padrão do back). O prestador costuma ter poucos
// contratos na empresa; a paginação só aparece quando passa de uma página.
const PAGE_SIZE = 50;

// Valor em destaque no cartão: só os tipos com valor no contrato
const VALUE_BY_TYPE = {
  FIXED: { field: "fixed_value", unit: "/mês" },
  HOURLY: { field: "hourly_rate", unit: "/hora" },
};

const BREADCRUMB = [{ page: "Meus contratos", route: "/e/contracts" }];

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
`;

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  > div {
    min-width: 0;
  }
`;

const Value = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.N100};
  color: ${({ theme }) => theme.colors.ACTION};

  svg {
    width: 14px;
  }
`;

const Pager = styled.div`
  margin-top: 20px;
`;

export default function ContractsEmployee() {
  const navigate = useNavigate();
  const contractService = useService("contracts");
  const [loading, setLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [data, setData] = useState({ list: [], totalRegisters: 0, totalPages: 1 });
  // Só a resposta da última requisição vale (troca de página rápida)
  const lastRequest = useRef(0);

  usePageHeader({ title: "Meus contratos", breadcrumb: BREADCRUMB });

  useEffect(() => {
    fetchContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  async function fetchContracts() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      // Sem scope=company: o back devolve só os contratos de quem chama na empresa ativa
      const result = await contractService.list(`page=${pageIndex + 1}&limit=${PAGE_SIZE}`);
      if (request !== lastRequest.current) return;
      const list = result?.data?.list || (Array.isArray(result?.data) ? result.data : []);
      setData({
        list,
        totalRegisters: result?.data?.totalRegisters ?? list.length,
        totalPages: result?.data?.totalPages || 1,
      });
    } catch (error) {
      if (request !== lastRequest.current) return;
      console.error(error);
      toast.error(error?.response?.data?.msg || "Erro ao carregar seus contratos");
      setData({ list: [], totalRegisters: 0, totalPages: 1 });
    }
    setLoading(false);
  }

  let content;
  if (loading) {
    content = (
      <Grid aria-busy="true">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <Skeleton $width="55%" $height="20px" $noFlex />
            <Skeleton $width="35%" $height="14px" $noFlex />
            <Skeleton $width="45%" $height="24px" $noFlex />
            <Skeleton $width="30%" $height="14px" $noFlex />
          </Card>
        ))}
      </Grid>
    );
  } else if (data.list.length === 0) {
    content = <NoResults notResultText="Nenhum contrato por aqui" />;
  } else {
    content = (
      <>
        <Grid>
          {data.list.map((contract) => (
            <ContractItem key={contract.id} contract={contract} navigate={navigate} />
          ))}
        </Grid>
        {data.totalPages > 1 ? (
          <Pager>
            <Pagination
              $pageIndex={pageIndex}
              $pageCount={data.totalPages}
              $totalRecords={data.totalRegisters}
              $pageSize={PAGE_SIZE}
              onChangePage={setPageIndex}
            />
          </Pager>
        ) : null}
      </>
    );
  }

  return (
    <ContentPage>
      <ContentBox>{content}</ContentBox>
    </ContentPage>
  );
}

function ContractItem({ contract, navigate }) {
  // Por hora com valor fixo e ajuste: o destaque é o fixo mensal; a hora só ajusta a jornada
  const fixedMode = isFixedWithAdjustment(contract);
  const value = fixedMode ? VALUE_BY_TYPE.FIXED : VALUE_BY_TYPE[contract.remuneration_type];
  const amount = value ? contract[value.field] : null;
  const route = `/e/contracts/${contract.id}`;

  return (
    // O cartão inteiro é o link: o href abre em outra aba pelo navegador; o clique
    // comum navega sem recarregar a página
    <Card
      $action={route}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(route);
      }}>
      <Top>
        <div>
          <Text $text={escapeHtml(contract.project?.name || "Contrato sem projeto")} $variant="h4_bold" $color="N900" />
          <Text $text={remunerationSummary(contract)} $variant="caption_medium" $color="N400" />
        </div>
        <DomainStatus domain="contrato" value={CONTRACT_STATUS[contract.status] || contract.status} />
      </Top>
      {amount ? (
        <Value>
          <Text $text={formatBRL(amount)} $variant="h3_bold" $color="N900" />
          <Text $text={value.unit} $variant="paragraph_small_medium" $color="N400" />
        </Value>
      ) : null}
      {fixedMode && amount && contract.hourly_rate ? (
        <Text
          $text="Ajuste de ± %s por hora fora da jornada"
          $replaces={[formatBRL(contract.hourly_rate)]}
          $variant="caption_medium"
          $color="N600"
        />
      ) : null}
      {contract.start_date ? (
        <Text $text={`Início ${formatDate(contract.start_date)}`} $variant="caption_medium" $color="N600" />
      ) : null}
      <Foot>
        <Text $text="Ver detalhes" $variant="paragraph_small_bold" $color="ACTION" />
        <ArrowLinkIcon />
      </Foot>
    </Card>
  );
}
